import { useElementMeasures } from '@hooks/useElementMeasures';
import { useEffect, useState, useSyncExternalStore } from 'react';
import { APP_SCROLL_CLASS } from './constants';
import { appScrollMedia, isAppHeaderScrolling } from './helpers';

const subscribe = (callback: () => void) => {
  appScrollMedia.addEventListener('change', callback);
  return () => appScrollMedia.removeEventListener('change', callback);
};

export const useIsAppScrollContainer = () =>
  useSyncExternalStore(subscribe, () => appScrollMedia.matches);

export const useAppScrollTop = (dependency?: unknown) => {
  const isContainer = useIsAppScrollContainer();
  const headerHeight = useElementMeasures('header', dependency).height;
  return isContainer || isAppHeaderScrolling() ? headerHeight : 0;
};

export const useAppScrollbarWidth = () => {
  const isContainer = useIsAppScrollContainer();
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const element = document.querySelector<HTMLElement>(`.${APP_SCROLL_CLASS}`);
    if (!isContainer || !element) {
      setWidth(0);
      return;
    }

    const update = () => setWidth(element.offsetWidth - element.clientWidth);
    const observer = new ResizeObserver(update);
    observer.observe(element);
    update();
    return () => observer.disconnect();
  }, [isContainer]);

  return width;
};

// Offset of the tenant subheader (`sticky-header`) for sticky elements inside the app scroll container.
// When the container scrolls, the subheader sits above it (subheader slot), so no offset is needed.
export const useStickyHeaderOffset = () => {
  const isContainer = useIsAppScrollContainer();
  const height = useElementMeasures('sticky-header').height;
  return isContainer ? 0 : height;
};
